import {
  DefaultOptions,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ApiContext from '@/context/ApiContext/ApiContext';
import convertQueryKeyToUrl from '@/helpers/convertQueryKeyToUrl';

const QueryClientContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { client } = useContext(ApiContext);

  const defaultOptions: DefaultOptions = useMemo(
    () => ({
      queries: {
        retry: 3,
        refetchInterval: 60 * 1000 * 5,
        staleTime: 30 * 1000,
        queryFn: async ({ queryKey }) => {
          const response = await client.current?.get<AxiosResponse>(
            convertQueryKeyToUrl(queryKey)
          );

          return response?.data;
        },
      },
    }),
    [client]
  );

  // based on https://tanstack.com/query/latest/docs/framework/react/guides/ssr#full-nextjs-pages-router-example
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions,
      })
  );

  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        ...defaultOptions.queries,
      },
      mutations: {
        // onError removed in React Query v5 - handle errors per-mutation or use error boundaries
      },
    });
  }, [defaultOptions.queries, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export default QueryClientContextProvider;
